import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { formatApiErrorDetail, validateGstin } from "../lib/api";
import { usePageMeta } from "../lib/seo";
import { Loader2, ArrowRight, CheckCircle2, XCircle } from "lucide-react";

export default function Login() {
  usePageMeta({
    title: "Sign In or Register Your MSME Business | KashBaaki",
    description:
      "Sign in to KashBaaki or register your Micro & Small Enterprise with GSTIN and Udyam to track overdue invoices, compute statutory interest under the MSMED Act, 2006, and issue demand notices to buyers.",
    path: "/login",
  });
  const { user, checked, login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const destFor = (u) => location.state?.from || (u?.role === "admin" ? "/admin" : "/cases");

  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "", company_name: "", gstin: "", udyam: "" });
  const [gstCheck, setGstCheck] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (checked && user) navigate(destFor(user), { replace: true });
  }, [checked, user]); // eslint-disable-line

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const checkGstin = async () => {
    if (!form.gstin) { setGstCheck(null); return; }
    try { setGstCheck(await validateGstin(form.gstin)); } catch { setGstCheck(null); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      let u;
      if (mode === "login") u = await login(form.email, form.password);
      else u = await register({
        name: form.name, email: form.email, password: form.password,
        company_name: form.company_name, gstin: form.gstin, udyam: form.udyam,
      });
      navigate(destFor(u), { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen paper-noise flex flex-col">
      <header className="border-b border-border bg-paper/85 backdrop-blur-md">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="login-brand">
            <img src="/kashbaaki-logo.png" alt="KashBaaki" className="h-8 w-auto object-contain" />
          </Link>
          <span className="hidden sm:flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.14em] text-muted-foreground">
            A <img src="/kashsam-logo.png" alt="Kashsam Data Solutions" className="h-4 w-auto object-contain" /> product
          </span>
        </div>
      </header>

      <main className="flex-1 px-4 py-16">
        <div className="max-w-5xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <section className="hidden lg:block" data-testid="login-info">
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-signal">Why KashBaaki</span>
            <h2 className="mt-3 font-serif text-4xl font-semibold tracking-tight text-ink leading-tight">
              Recover what's baaki — with the law on your side
            </h2>
            <p className="mt-5 text-sm leading-relaxed text-muted-foreground">
              Indian Micro &amp; Small Enterprises lose lakhs to delayed B2B payments every year.
              The MSMED Act, 2006 already entitles you to your outstanding dues plus statutory
              interest at three times the RBI Bank Rate, compounded with monthly rests — yet most
              businesses never claim it. KashBaaki turns that statutory right into a simple, guided
              recovery workflow you can run from your laptop.
            </p>
            <ul className="mt-6 space-y-3 text-sm text-ink">
              <li className="flex items-start gap-3"><CheckCircle2 className="h-4 w-4 text-success mt-0.5 shrink-0" strokeWidth={1.75} /> Track every overdue invoice and its 45-day statutory due date in one place</li>
              <li className="flex items-start gap-3"><CheckCircle2 className="h-4 w-4 text-success mt-0.5 shrink-0" strokeWidth={1.75} /> Auto-calculate compound interest under Section 16 with monthly rests</li>
              <li className="flex items-start gap-3"><CheckCircle2 className="h-4 w-4 text-success mt-0.5 shrink-0" strokeWidth={1.75} /> Generate demand notices and email them to buyers with a delivery audit trail</li>
              <li className="flex items-start gap-3"><CheckCircle2 className="h-4 w-4 text-success mt-0.5 shrink-0" strokeWidth={1.75} /> Escalate stuck cases through MSEFC / Samadhaan conciliation when needed</li>
            </ul>
            <p className="mt-6 text-xs leading-relaxed text-muted-foreground">
              Registration captures your GSTIN and Udyam registration so your notices carry the
              right statutory footing. Informational tool, not legal advice — consult a qualified
              advocate before serving any notice.
            </p>
          </section>

          <div className="w-full max-w-md lg:justify-self-end bg-white border border-border doc-shadow p-8">
          <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-signal">
            {mode === "login" ? "Sign in" : "Create your account"}
          </span>
          <h1 className="mt-2 font-serif text-4xl font-semibold tracking-tight text-ink">
            {mode === "login" ? "Welcome back" : "Recover what's baaki"}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {mode === "login"
              ? "Sign in to manage your cases and the KashBaaki ops console."
              : "Register your business to start tracking overdue invoices."}
          </p>

          <form onSubmit={submit} className="mt-7 space-y-4" data-testid="login-form">
            {mode === "register" && (
              <>
                <Field label="Your name">
                  <input data-testid="login-name" value={form.name} onChange={set("name")} className="baaki-input" placeholder="Your name" />
                </Field>
                <Field label="Company name *">
                  <input data-testid="login-company" value={form.company_name} onChange={set("company_name")} className="baaki-input" placeholder="Registered business name" required />
                </Field>
                <Field label="GSTIN *" hint="15-character GST number; verified by format & checksum">
                  <input data-testid="login-gstin" value={form.gstin} onChange={set("gstin")} onBlur={checkGstin}
                    className="baaki-input font-mono uppercase" placeholder="27AAPFU0939F1ZV" maxLength={15} required />
                </Field>
                {gstCheck && (
                  <div data-testid="gst-check" className={`flex items-center gap-2 text-xs -mt-2 ${gstCheck.valid ? "text-success" : "text-danger"}`}>
                    {gstCheck.valid ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                    {gstCheck.message}
                  </div>
                )}
                <Field label="Udyam / MSME registration no. *" hint="Format: UDYAM-XX-00-0000000">
                  <input data-testid="login-udyam" value={form.udyam} onChange={set("udyam")} className="baaki-input font-mono" placeholder="UDYAM-KR-03-0001234" required />
                </Field>
              </>
            )}
            <Field label="Email">
              <input data-testid="login-email" type="email" value={form.email} onChange={set("email")} className="baaki-input" placeholder="you@company.in" required />
            </Field>
            <Field label="Password">
              <input data-testid="login-password" type="password" value={form.password} onChange={set("password")} className="baaki-input" placeholder="••••••••" required />
            </Field>

            {error && (
              <div data-testid="login-error" className="bg-danger-bg border border-danger/30 px-4 py-2.5 text-sm text-danger">{error}</div>
            )}

            <button data-testid="login-submit" type="submit" disabled={loading}
              className="group w-full bg-signal text-white font-medium text-sm py-3.5 hover:bg-signal-dark hover:-translate-y-[1px] transition-transform duration-150 disabled:opacity-60 flex items-center justify-center gap-2">
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {loading ? "Please wait..." : mode === "login" ? "Sign in" : "Create account"}
              {!loading && <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform duration-150" strokeWidth={1.5} />}
            </button>
          </form>

          <button data-testid="login-toggle-mode"
            onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); setGstCheck(null); }}
            className="mt-5 text-sm text-muted-foreground hover:text-ink transition-colors duration-150">
            {mode === "login" ? "New to KashBaaki? Register your business" : "Already have an account? Sign in"}
          </button>
        </div>
        </div>
      </main>
    </div>
  );
}

const Field = ({ label, hint, children }) => (
  <label className="block">
    <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-muted-foreground">{label}</span>
    {children}
    {hint && <span className="mt-1 block text-[11px] text-muted-foreground">{hint}</span>}
  </label>
);
